import { createPortal } from 'react-dom';
import { X, Building2, Check, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const ALL_ID = 'all';

type DashboardDepartmentPickerModalProps = {
	isOpen: boolean;
	onClose: () => void;
	departments: CompanyDepartments.CompanyDepartment[];
	isLoading?: boolean;
	selectedId: string | typeof ALL_ID;
	onSelect: (departmentId: string | typeof ALL_ID) => void;
};

export function DashboardDepartmentPickerModal({
	isOpen,
	onClose,
	departments,
	isLoading = false,
	selectedId,
	onSelect,
}: DashboardDepartmentPickerModalProps) {
	if (!isOpen) return null;

	return createPortal(
		<div className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4">
			<div
				className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"
				onClick={onClose}
			/>
			<div className="animate-in zoom-in-95 relative flex max-h-[90vh] w-full max-w-[520px] flex-col overflow-hidden rounded-[2rem] bg-white shadow-2xl duration-200">
				<div className="flex shrink-0 items-center justify-between border-b border-stone-50 px-4 py-4 sm:px-8 sm:py-6">
					<h2 className="text-lg font-black text-stone-900">
						Escolher departamento
					</h2>
					<button
						type="button"
						onClick={onClose}
						className="text-stone-400 transition-colors hover:text-stone-600"
					>
						<X size={20} />
					</button>
				</div>

				<div className="flex-1 overflow-y-auto p-4 sm:p-8">
					{isLoading ? (
						<div className="flex flex-col items-center justify-center py-12">
							<Loader2 className="size-8 animate-spin text-stone-400" />
						</div>
					) : (
						<div className="space-y-2">
							<button
								type="button"
								onClick={() => {
									onSelect(ALL_ID);
									onClose();
								}}
								className={cn(
									'flex w-full items-center gap-3 rounded-2xl border p-4 text-left transition-colors',
									selectedId === ALL_ID
										? 'border-[#e11d74]/40 bg-pink-50/50'
										: 'border-stone-100 bg-stone-50/50 hover:border-stone-200 hover:bg-white',
								)}
							>
								<Building2 className="size-5 shrink-0 text-stone-500" />
								<span className="flex-1 text-sm font-bold text-stone-900">
									Todos
								</span>
								{selectedId === ALL_ID ? (
									<Check className="size-5 shrink-0 text-[#e11d74]" />
								) : null}
							</button>
							{departments.map((d) => {
								const sel = selectedId === d.id;
								return (
									<button
										key={d.id}
										type="button"
										onClick={() => {
											onSelect(d.id);
											onClose();
										}}
										className={cn(
											'flex w-full items-center gap-3 rounded-2xl border p-4 text-left transition-colors',
											sel
												? 'border-[#e11d74]/40 bg-pink-50/50'
												: 'border-stone-100 bg-stone-50/50 hover:border-stone-200 hover:bg-white',
										)}
									>
										<Building2 className="size-5 shrink-0 text-stone-500" />
										<span className="flex-1 text-sm font-bold text-stone-900">
											{d.name}
										</span>
										{sel ? (
											<Check className="size-5 shrink-0 text-[#e11d74]" />
										) : null}
									</button>
								);
							})}
						</div>
					)}
				</div>
			</div>
		</div>,
		document.body,
	);
}
